/** 在图片特效渲染中兼容历史基础投影的异常外扩坐标。 */
import { IImageEffectShadowType, type IImageLayerProps } from '@/version-types';
import { ImgEffectResultImgSize } from './constants';

type EffectPosition = NonNullable<IImageLayerProps['effect']>['effectPosition'];

/** 超过此倍数时，历史基础投影边界才需要兼容。 */
const MAX_EFFECT_EXPANSION = 16;
/** 正常基础投影画布中，原图距离画布边缘的比例为 1/7。 */
const BASE_SHADOW_MARGIN_RATIO = (ImgEffectResultImgSize - 1) / (2 * ImgEffectResultImgSize);
const MARGIN_TOLERANCE = 0.02;

/**
 * 将历史基础投影的异常外扩坐标缩放到图片图层尺寸，保留已有特效截图
 *
 * @param position - 特效外扩边界
 * @param width - 图片图层宽度
 * @param height - 图片图层高度
 * @param effect - 图片特效配置
 * @returns 命中历史基础投影异常时返回缩放边界；其他有效边界保持原值
 */
export const getCompatibleImageEffectPosition = (
    position: EffectPosition,
    width: number,
    height: number,
    effect?: IImageLayerProps['effect'],
): EffectPosition => {
    if (!position || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return undefined;
    }

    const { left, top, right, bottom } = position;
    if (![left, top, right, bottom].every(Number.isFinite) || right <= left || bottom <= top) return undefined;

    const spanX = right - left;
    const spanY = bottom - top;
    const isOversized =
        Math.max(Math.abs(left), Math.abs(right), spanX) > width * MAX_EFFECT_EXPANSION ||
        Math.max(Math.abs(top), Math.abs(bottom), spanY) > height * MAX_EFFECT_EXPANSION;
    if (!isOversized) return position;

    const isBaseShadowOnly =
        effect?.shadow?.length === 1 &&
        effect.shadow[0].type === IImageEffectShadowType.base &&
        !effect.shadow[0].hidden &&
        !effect.glow?.some((item) => !item.hidden) &&
        !effect.stroke?.some((item) => !item.hidden);
    const hasExpectedMargins =
        Math.abs(-left / spanX - BASE_SHADOW_MARGIN_RATIO) <= MARGIN_TOLERANCE &&
        Math.abs(-top / spanY - BASE_SHADOW_MARGIN_RATIO) <= MARGIN_TOLERANCE &&
        Math.abs(right / spanX - (1 - BASE_SHADOW_MARGIN_RATIO)) <= MARGIN_TOLERANCE &&
        Math.abs(bottom / spanY - (1 - BASE_SHADOW_MARGIN_RATIO)) <= MARGIN_TOLERANCE;
    if (!isBaseShadowOnly || !hasExpectedMargins) return position;

    const scaleX = (width * ImgEffectResultImgSize) / spanX;
    const scaleY = (height * ImgEffectResultImgSize) / spanY;
    return { left: left * scaleX, top: top * scaleY, right: right * scaleX, bottom: bottom * scaleY };
};
